import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

const validationSchema = Yup.object().shape({
  profilePicture: Yup.mixed()
    .required('Profile picture is required')
    .test('fileFormat', 'Unsupported file format', (value) => {
      if (value) {
        const supportedFormats = ['image/jpg', 'image/jpeg', 'image/png'];
        return supportedFormats.includes(value.type);
      }
      return false;
    })
    .test('fileSize', 'File size is too large', (value) => {
      if (value) {
        return value.size <= 2 * 1024 * 1024; // 2MB
      }
      return false;
    }),
  hospitalName: Yup.string()
    .trim()
    .required('Hospital Name is required'),
  hospitalBio: Yup.string()
    .trim()
    .required('Hospital Bio is required'),
  hospitalSpecialization: Yup.string()
    .trim()
    .required('A specialization is required')
    .matches(/^(Cardiology|Neurology|Pediatrics|Oncology|Orthopedics|Gastroenterology|Urology|Psychiatry|Radiology|Dermatology|Endocrinology|Nephrology|Gynecology|Ophthalmology|Otolaryngology|Hematology|Infectious Disease|Rheumatology|Allergy and Immunology|Pulmonology|Neurosurgery|Plastic Surgery|Emergency Medicine|Anesthesiology|Pathology)$/, 'Invalid hospital specialization')
});

const PersonalInformation = ({ initialData = {}, onComplete, onBack }) => {
  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const csrfToken = getCookie('csrftoken');

  // Set the CSRF token in the default headers
  axios.defaults.headers.common['X-CSRFToken'] = csrfToken;

  // const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // const [submitting, setSubmitting] = useState('');

  const SPECIALIZATION_CHOICES = [
    { value: 'general', label: 'General Medicine' },
    { value: 'orthopedics', label: 'Orthopedics' },
    { value: 'neurology', label: 'Neurology' },
    { value: 'pediatrics', label: 'Pediatrics' },
    { value: 'dermatology', label: 'Dermatology' },
    { value: 'psychiatry', label: 'Psychiatry' },
    { value: 'cardiology', label: 'Cardiology' },
    { value: 'gynecology', label: 'Gynecology' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    specialization: '',
    profile_picture: null
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, profile_picture: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('name', formData.name);
    data.append('bio', formData.bio);
    data.append('specialization', formData.specialization);
    data.append('profile_picture', formData.profile_picture);

    try {
      const response = await axios.post('http://127.0.0.1:8000/hospital/profile', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Success:', response.data);
      if (response.status === 201) {
        setSuccess('Personal Information submitted successfully!');
        setError(''); // Reset error on successful submission
        setFormData([]); // Clear the form fields

        onComplete({
          name: values.hospitalName,
          bio: values.hospitalBio,
          specialization: values.hospitalSpecialization,
          profile_picture: values.profilePicture
        }, true); // Pass true for isValid
      } else {
        throw new Error('Personal information submission failed. Please try again.');
      }
    } catch (error) {
      console.log('');
    }
  };

  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Personal Information</h2>
      {error && <div className={css(styles.error)}>{error}</div>}
      {success && <div className={css(styles.success)}>{success}</div>}
      <Formik
        initialValues={{
          profilePicture: initialData?.profilePicture || '',
          hospitalName: initialData?.hospitalName || '',
          hospitalBio: initialData?.hospitalBio || '',
          hospitalSpecialization: initialData?.hospitalSpecialization || ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <label htmlFor='profilePicture' className={css(styles.label)}>
              Profile Picture
            </label>
            <div className={css(styles.fileUploadContainer)}>
              <input
                type='file'
                name='profile_picture'
                accept='image/*'
                onChange={handleFileChange}
                className={css(styles.fileInput)}
              />
              {error && <p className={css(styles.error)}>{error}</p>}
              {success && <p className={css(styles.success)}>{success}</p>}
            </div>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='hospitalName' className={css(styles.label)}>
                Hospital Name
              </label>
              <input
                type='text'
                name='name'
                placeholder='Hospital Name'
                className={css(styles.input)}
                onChange={handleChange}
                required
              />
              {errors.hospitalName && touched.hospitalName && (
                <div className={css(styles.error)}>{errors.hospitalName}</div>
              )}
            </div>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='hospitalBio' className={css(styles.label)}>
                Hospital Bio
              </label>
              <textarea
                name='bio'
                className={css(styles.input)}
                placeholder='Hospital Bio'
                onChange={handleChange}
                required
              />
              {errors.hospitalBio && touched.hospitalBio && (
                <div className={css(styles.error)}>{errors.hospitalBio}</div>
              )}
            </div>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='hospitalSpecialization' className={css(styles.label)}>
                Hospital Specialization
              </label>
              {/* Dropdown for Specialization */}
              <select
                name='specialization'
                className={css(styles.input)}
                onChange={handleChange}
                value={formData.specialization}
                required
              >
                {SPECIALIZATION_CHOICES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.hospitalSpecialization && touched.hospitalSpecialization && (
                <div className={css(styles.error)}>{errors.hospitalSpecialization}</div>
              )}
            </div>

            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
                  styles.button,
                  styles.nextButton
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
              >
                Save
              </motion.button>
              <motion.button
                type='button'
                className={css(
                  styles.button,
                  styles.nextButton
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (!isValid) {
                    console.log('Form is invalid, staying on the same page.');
                    alert('Please fill in all required fields before proceeding.');
                    return;
                  }
                  console.log('Form is valid, navigating to the next section.');
                  onComplete(); // Ensure this function correctly updates the state for the next section
                }}
              >
                Next
              </motion.button>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  label: {
    fontSize: '1rem',
    fontWeight: '100',
    marginBottom: '1rem',
    color: '#4a5568'
  },
  fileInput: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '2px',
    border: '1px solid #e2e8f0'
  },
  fileUploadContainer: {
    border: '1px dashed rgb(66, 86, 108)',
    borderRadius: '4px',
    borderColor: '#2783F7',
    background: '#f0f7ff',
    textAlign: 'center',
    cursor: 'pointer',
    color: 'rgb(51, 66, 82)',
    transition: 'all 0.3s ease',
    ':hover': {
      borderColor: '#2783F7',
      background: '#e0f2fe'
    }
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    color: '#4a5568'
  },
  success: {
    color: 'green',
    marginBottom: '1rem'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  nextButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  }
});

export default PersonalInformation;
